import { useCallback, useEffect, useMemo, useState } from 'react';
import type { BoothPanelProps } from '../panels';
import type { ProductSizeInterface } from '../../../../../../Interface/ProductSize';
import LineSheetToolbar from './LineSheetToolbar/LineSheetToolbar';
import type {
  LineSheetOwnership,
  LineSheetSort,
} from './LineSheetToolbar/LineSheetToolbar';
import LineSheetRow from './LineSheetRow/LineSheetRow';
import useBoothProducts from './useBoothProducts';
import useBoothBasket from './useBoothBasket';
import type { BasketPositionInput } from './useBoothBasket';
import { formatNumber, lineSheetPrice } from './pricing';
import styles from './AssortmentPanel.module.scss?module';

const SKELETON_ROWS = 3;
// Numeric, so "Pure (750g)" comes before "Pure (1500g)".
const nameCollator = new Intl.Collator('en', {
  sensitivity: 'base',
  numeric: true,
});

/**
 * Assortment of a brand booth as a wholesale line sheet (P-4889): search, "My products",
 * category and sort on top, one row per item, expanded the flavour matrix with quantity fields
 * and one "Add to basket" per item.
 */
export default function AssortmentPanel({
  exhibitor,
  tab,
  onBadge,
  track,
}: BoothPanelProps) {
  const {
    items,
    isLoading,
    isError,
    stockedErpIds,
    basketUnitsByAttributeId,
    refetchBasket,
  } = useBoothProducts(exhibitor.id);
  const { addPositions, statusByProductSizeId } = useBoothBasket(
    track,
    refetchBasket
  );

  const [search, setSearch] = useState('');
  const [ownership, setOwnership] = useState<LineSheetOwnership>('all');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState<LineSheetSort>('relevance');
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<number>>(
    () => new Set()
  );
  /** Entered quantity per flavour (shop id); kept while rows are filtered or collapsed. */
  const [quantities, setQuantities] = useState<Record<number, number>>({});

  useEffect(() => {
    if (isError) {
      onBadge(tab, null);
    } else if (!isLoading) {
      onBadge(tab, items.length);
    }
  }, [tab, onBadge, isLoading, isError, items.length]);

  const categories = useMemo(() => {
    const names = new Set<string>();
    items.forEach((item) => {
      const name = item.category?.name?.trim();
      if (name) {
        names.add(name);
      }
    });

    return Array.from(names).sort(nameCollator.compare);
  }, [items]);

  // "Products you stock" only makes sense when the customer stocks one of this booth's items.
  const canFilterOwnership = useMemo(
    () =>
      stockedErpIds !== null &&
      items.some((item) => stockedErpIds.has(item.erp_id)),
    [items, stockedErpIds]
  );
  const activeOwnership: LineSheetOwnership = canFilterOwnership
    ? ownership
    : 'all';
  const activeCategory = categories.includes(category) ? category : '';

  const visibleItems = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('en');
    const matches = items.filter((item) => {
      if (
        activeOwnership === 'stocked' &&
        !(stockedErpIds?.has(item.erp_id) ?? false)
      ) {
        return false;
      }
      if (activeCategory && item.category?.name?.trim() !== activeCategory) {
        return false;
      }
      if (needle) {
        const haystack = [
          item.name,
          item.category?.name,
          ...(item.product_attributes ?? []).map((flavour) => flavour.name),
        ]
          .filter((part): part is string => typeof part === 'string')
          .join(' ')
          .toLocaleLowerCase('en');

        return haystack.includes(needle);
      }

      return true;
    });

    return sortItems(matches, sort);
  }, [items, search, activeOwnership, activeCategory, stockedErpIds, sort]);

  const toggleItem = useCallback(
    (erpId: number) => {
      const opening = !expandedIds.has(erpId);
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (opening) {
          next.add(erpId);
        } else {
          next.delete(erpId);
        }

        return next;
      });
      if (opening) {
        track('item_expanded', { product_size_id: erpId });
      }
    },
    [expandedIds, track]
  );

  const changeQuantity = useCallback(
    (attributeId: number, quantity: number) => {
      setQuantities((prev) =>
        prev[attributeId] === quantity
          ? prev
          : { ...prev, [attributeId]: quantity }
      );
    },
    []
  );

  const addItem = useCallback(
    async (item: ProductSizeInterface) => {
      const price = lineSheetPrice(item).current ?? 0;
      const positions: BasketPositionInput[] = (item.product_attributes ?? [])
        .map((flavour) => ({
          product_attribute_id: flavour.id,
          quantity: quantities[flavour.id] ?? 0,
          price,
        }))
        .filter((position) => position.quantity > 0);

      const added = await addPositions(positions, {
        productSizeId: item.erp_id,
      });
      if (added) {
        setQuantities((prev) => {
          const next = { ...prev };
          positions.forEach((position) => {
            delete next[position.product_attribute_id];
          });

          return next;
        });
      }
    },
    [quantities, addPositions]
  );

  const isFiltered =
    search.trim() !== '' || activeOwnership !== 'all' || activeCategory !== '';
  const countLabel =
    isFiltered && !isLoading && !isError
      ? formatNumber(visibleItems.length) +
        ' of ' +
        formatNumber(items.length) +
        ' ' +
        (items.length === 1 ? 'product' : 'products')
      : formatNumber(items.length) +
        ' ' +
        (items.length === 1 ? 'product' : 'products');

  return (
    <section className={styles.panel} aria-busy={isLoading}>
      <h2 className={styles.heading}>
        Assortment
        {!isLoading && !isError && (
          <span className={styles.headingCount}> · {countLabel}</span>
        )}
      </h2>

      {isError ? (
        <p className={styles.message} role="alert">
          The assortment could not be loaded.
        </p>
      ) : isLoading ? (
        <ul className={styles.rows} aria-label="Loading products">
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <li key={index} className={styles.skeletonRow} />
          ))}
        </ul>
      ) : items.length === 0 ? (
        <p className={styles.message}>No products listed for this booth yet.</p>
      ) : (
        <>
          <LineSheetToolbar
            search={search}
            onSearchChange={setSearch}
            ownership={activeOwnership}
            onOwnershipChange={setOwnership}
            showOwnership={canFilterOwnership}
            category={activeCategory}
            categories={categories}
            onCategoryChange={setCategory}
            sort={sort}
            onSortChange={setSort}
          />
          {visibleItems.length === 0 ? (
            <p className={styles.message} role="status">
              No products match your search.
            </p>
          ) : (
            <ul className={styles.rows}>
              {visibleItems.map((item) => (
                <LineSheetRow
                  key={item.erp_id}
                  item={item}
                  isStocked={stockedErpIds?.has(item.erp_id) ?? false}
                  expanded={expandedIds.has(item.erp_id)}
                  onToggle={toggleItem}
                  quantities={quantities}
                  onQuantityChange={changeQuantity}
                  basketUnitsByAttributeId={basketUnitsByAttributeId}
                  status={statusByProductSizeId[item.erp_id] ?? 'idle'}
                  onAdd={addItem}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

/** Sorts a copy of the items; relevance keeps the API order. */
function sortItems(
  items: ProductSizeInterface[],
  sort: LineSheetSort
): ProductSizeInterface[] {
  if (sort === 'relevance') {
    return items;
  }
  const sorted = [...items];
  if (sort === 'name') {
    sorted.sort((a, b) => nameCollator.compare(a.name, b.name));

    return sorted;
  }
  const direction = sort === 'price-asc' ? 1 : -1;
  sorted.sort((a, b) => {
    const priceA = lineSheetPrice(a).current;
    const priceB = lineSheetPrice(b).current;
    // Items without a price go last in both directions.
    if (priceA === null || priceB === null) {
      return (priceA === null ? 1 : 0) - (priceB === null ? 1 : 0);
    }

    return (priceA - priceB) * direction;
  });

  return sorted;
}
